import Ingredient from '@/models/ingredient';
import OrderProductIngredient from '@/models/orderProductIngredient';
import OrderProductInterface from '@/models/orderProductInterface';
import Product from '@/models/product';

export type GroupIngredients = {
  optional: OrderProductIngredient[],
  oneOf: {
    [key: string]: OrderProductIngredient
  }
};

export type GroupIngredientsProps = {
  optional: Ingredient[],
  oneOf: {
    [key: string]: Ingredient
  }
};

export default class PosOrderProduct implements OrderProductInterface {
  product: Product | null = null;
  public groupIngredients: GroupIngredients = {
    optional: [],
    oneOf: {}
  }
  id: string = '';
  count: number = 1;
  refundCount: number = 0;
  doneCount: number = 0;
  isChecked: boolean = false;
  gtins: string[] = [];

  public static createByProductAndIngredients({ product, ingredients, gtin }: { product: Product, ingredients: GroupIngredientsProps, gtin: string }) {
    const orderProduct = new PosOrderProduct();

    orderProduct.product = product;
    orderProduct.groupIngredients.optional = ingredients.optional.map(OrderProductIngredient.createByOriginalIngredient);

    if (gtin !== '') {
      orderProduct.gtins.push(gtin);
    }

    for (const key in ingredients.oneOf) {
      if (ingredients.oneOf.hasOwnProperty(key)) {
        orderProduct.groupIngredients.oneOf[key] = OrderProductIngredient.createByOriginalIngredient(ingredients.oneOf[key]);
      }
    }

    return orderProduct;
  }

  get ingredients(): OrderProductIngredient[] {
    return this.groupIngredients.optional.concat(Object.values(this.groupIngredients.oneOf));
  }

  get hash(): string {
    if (this.product !== null) {
      const ingredientsHash = this.ingredients
        .reduce((ingredientIdList: string[], orderProductIngredient: OrderProductIngredient) => {
          ingredientIdList.push(orderProductIngredient.hash);

          return ingredientIdList;
        }, [])
        .sort((firstId: string, secondId: string) => firstId > secondId ? 1 : -1)
        .reduce((hash: string, ingredientId: string | null) => hash + `-${ingredientId}`, '');

      return `${this.product.id}${ingredientsHash}`;
    }

    return '';
  }

  get cost() {
    if (this.product !== null) {
      const ingredientsCost = this.ingredients.reduce((sum: number, ingredient: OrderProductIngredient) => {
        return sum + ingredient.fullCost;
      }, 0);

      return Math.round((this.product.cost + ingredientsCost) * 100) / 100;
    }

    return 0;
  }

  get ingredientsCostWithoutDiscount() {
    return this.ingredients.reduce(
      (sum: number, ingredient: OrderProductIngredient) => sum + ingredient.fullCostWithoutDiscount,
      0,
    );
  }

  get productCost() {
    return this.product !== null ? this.product.cost : 0;
  }

  get isHaveIngredients(): boolean {
    return this.ingredients.length > 0;
  }

  get isDone() {
    return this.doneCount === this.count;
  }

  public increment(gtin: string) {
    if (gtin) {
      this.gtins.push(gtin);
    }

    this.count++;
  }

  public decrement(gtin: string) {
    if (this.count > 0) {
      this.count--;
    }

    if (gtin) {
      const index = this.gtins.findIndex(( other: string ) => other === gtin);

      if (index !== -1) {
        this.gtins.splice(index, 1);
      }
    }
  }

  public toggleChecked() {
    this.isChecked = !this.isChecked;
  }

  public done() {
    if (this.count > this.doneCount) {
      this.doneCount++;
    }
  }

  public undone() {
    if (this.doneCount > 0) {
      this.doneCount--;
    }
  }
}
